import { Visibility, VisibilityOff } from "@mui/icons-material";
import { FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput } from "@mui/material";
import { useMemo, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";

interface Props<T extends {}> {
  name: keyof T;
  label?: string;
  icon?: React.ReactNode | null;
  multiline?: boolean;
  rows?: string | number;
  minRows?: string | number;
  maxRows?: string | number;
  fullWidth?: boolean;
  required?: boolean;
  readOnly?: boolean;
  labelSize?: 'normal' | 'large';
  autoComplete?: string;
  reactHookForm: UseFormReturn<T>;
}

function DefaultFormPasswordInput<T extends {}>({
  name,
  label,
  icon,
  multiline,
  rows,
  minRows,
  maxRows,
  fullWidth,
  readOnly,
  labelSize,
  autoComplete,
  reactHookForm,
  ...props
}: Props<T>) {

  const [showPassword, setShowPassword] = useState(false)

  const handleClickShowPassword = () => setShowPassword((show: boolean) => !show)

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const labelFontSize = useMemo(() => {
    const size = labelSize ?? 'normal';
    switch (size) {
      case 'normal':
        return '1.0rem';

      case 'large':
        return '1.25rem';
    }
  }, [
    labelSize,
  ]);

  const labelFontWeight = useMemo(() => {
    const size = labelSize ?? 'normal';
    switch (size) {
      case 'normal':
        return 'normal';

      case 'large':
        return 'bold';
    }
  }, [
    labelSize,
  ]);

  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let value = '';

        let helperText: string | null = null;
        let shrink = false;

        if (typeof field.value === 'string') {
          value = field.value;
        }
        else if (typeof field.value === 'number') {
          value = field.value.toString();
        }
        else if (typeof field.value === 'boolean') {
          value = field.value.toString();
        }

        if (field.value !== undefined && field.value !== null) {
          if (typeof field.value === 'string') {
            if (field.value.length > 0) {
              shrink = true;
            }
          } else {
            shrink = true;
          }
        }

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        return <FormControl
          sx={{ width: '100%', minHeight: 45 }}
          variant="outlined"
          size="small">
          <InputLabel
            htmlFor={`form-input__${name as string}`}
            shrink={shrink}
            required={props.required}
            sx={{ fontSize: shrink === true ? labelFontSize : '1.0rem', fontWeight: labelFontWeight }}
            error={helperText !== null}>
            {label}
          </InputLabel>

          <OutlinedInput
            {...field}
            id={`form-input__${name as string}`}
            label={(value && value.length > 0) ? label : null}
            size="small"
            type={showPassword ? 'text' : 'password'}
            multiline={multiline}
            rows={rows}
            minRows={minRows}
            maxRows={maxRows}
            value={value ?? ''}
            aria-describedby={`${name as string}-component-error-text`}
            readOnly={readOnly}
            autoComplete={autoComplete}
            error={helperText !== null}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword ? 'Esconder senha' : 'Mostrar senha'
                  }
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            required={props.required}
            fullWidth={fullWidth}
            sx={{
              '&.MuiOutlinedInput-root': {
                '& .MuiOutlinedInput-notchedOutline': {
                  fontSize: labelFontSize,
                  fontWeight: labelFontWeight,
                }
              }
            }}
          />
          <FormHelperText id={`${name as string}-component-error-text`} error={helperText !== null && helperText !== undefined}>
            {helperText}
          </FormHelperText>
        </FormControl >
      }} />
  );
}

export default DefaultFormPasswordInput;